/**
 * PodService — API client for pod management endpoints.
 * All requests include the tracker user ID in headers for auth.
 *
 * Backed by PocketBase collections 'pods' and 'pod_members'.
 */

function getApiBase() {
    // Use VITE_PROXY_URL if set (points to the backend proxy server),
    // otherwise fall back to relative /api (works when vite proxy is configured)
    const proxyUrl = typeof import.meta !== 'undefined' && import.meta.env?.VITE_PROXY_URL;
    return proxyUrl ? `${proxyUrl.replace(/\/+$/, '')}/api` : '/api';
}

export default class PodService {
    constructor(trackerUserId, getToken) {
        this._userId = trackerUserId;
        this._getToken = getToken; // Clerk getToken() for JWT auth
        this._apiBase = getApiBase();
    }

    async _headers(json = false) {
        const h = { 'x-tracker-user-id': this._userId };
        // Attach Clerk JWT when available for verified auth
        if (this._getToken) {
            try {
                const token = await this._getToken();
                if (token) h['Authorization'] = `Bearer ${token}`;
            } catch (_) { /* fall back to x-tracker-user-id */ }
        }
        if (json) h['Content-Type'] = 'application/json';
        return h;
    }

    async getMyPods() {
        const res = await fetch(`${this._apiBase}/pods/mine`, { headers: await this._headers() });
        if (!res.ok) throw new Error(`Failed to fetch pods: ${res.status}`);
        return res.json();
    }

    async createPod(name) {
        const res = await fetch(`${this._apiBase}/pods`, {
            method: 'POST',
            headers: await this._headers(true),
            body: JSON.stringify({ name }),
        });
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.error || `Failed to create pod: ${res.status}`);
        }
        return res.json();
    }

    async joinPod(inviteCode) {
        const res = await fetch(`${this._apiBase}/pods/join`, {
            method: 'POST',
            headers: await this._headers(true),
            body: JSON.stringify({ inviteCode }),
        });
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.error || `Failed to join pod: ${res.status}`);
        }
        return res.json();
    }

    async updateSharing(memberId, prefs) {
        const res = await fetch(`${this._apiBase}/pod-members/${memberId}`, {
            method: 'PATCH',
            headers: await this._headers(true),
            body: JSON.stringify(prefs),
        });
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.error || `Failed to update sharing: ${res.status}`);
        }
        return res.json();
    }

    async leavePod(memberId) {
        const res = await fetch(`${this._apiBase}/pod-members/${memberId}`, {
            method: 'DELETE',
            headers: await this._headers(),
        });
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.error || `Failed to leave pod: ${res.status}`);
        }
        return res.json();
    }

    async removeMember(memberId) {
        const res = await fetch(`${this._apiBase}/pod-members/${memberId}`, {
            method: 'DELETE',
            headers: await this._headers(),
        });
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.error || `Failed to remove member: ${res.status}`);
        }
        return res.json();
    }

    async updatePod(podId, updates) {
        const res = await fetch(`${this._apiBase}/pods/${podId}`, {
            method: 'PATCH',
            headers: await this._headers(true),
            body: JSON.stringify(updates),
        });
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.error || `Failed to update pod: ${res.status}`);
        }
        return res.json();
    }

    async transferOwnership(memberId) {
        const res = await fetch(`${this._apiBase}/family-members/${memberId}/transfer-ownership`, {
            method: 'PATCH',
            headers: await this._headers(true),
            body: JSON.stringify({}),
        });
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.error || `Failed to transfer ownership: ${res.status}`);
        }
        return res.json();
    }

    async getPendingRequests() {
        const res = await fetch(`${this._apiBase}/access-requests`, {
            headers: await this._headers(),
        });
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.error || `Failed to fetch requests: ${res.status}`);
        }
        return res.json();
    }

    async reviewRequest(requestId, action, podId) {
        const body = { action };
        if (podId) body.podId = podId;
        const res = await fetch(`${this._apiBase}/access-requests/${requestId}`, {
            method: 'PATCH',
            headers: await this._headers(true),
            body: JSON.stringify(body),
        });
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.error || `Failed to ${action} request: ${res.status}`);
        }
        return res.json();
    }

}
